import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData } from "@tanstack/react-query";
import { fetchJson, queryClient } from "@/lib/queryClient";
import { useDebounce } from "@/hooks/useDebounce";
import {
  CONTACT_LIST_MAX_LIMIT,
  CONTACT_LIST_PAGE,
  contactListSearch,
  type ContactListParams,
  type ContactListResponse,
  type ContactSummary,
} from "@shared/contactList";
export { toServerFilters } from "@shared/contactList";
import type { Contact } from "@shared/schema";
import { trimInfiniteListsWhenIdle } from "@/components/ui/infinite-list-trim";

/**
 * The Contacts list as the server pages it — see shared/contactList.ts.
 *
 * Every key starts `['/api/contacts', projectId, …]`, so the invalidations the
 * rest of the app already makes on that prefix (a saved contact, an import, a
 * sale that created a purchaser) refresh the list and its stat cards too.
 */

export type ContactListPage = ContactListResponse<Contact>;
export type ContactListData = InfiniteData<ContactListPage, unknown>;
type ViewParams = Omit<ContactListParams, "offset" | "limit" | "ids">;

export function contactListKey(params: ViewParams) {
  const { projectId, ...view } = params;
  return ["/api/contacts", projectId, "list", view] as const;
}

export function contactSummaryKey(projectId: string | null | undefined, owner: string) {
  return ["/api/contacts", projectId, "summary", owner] as const;
}

export function fetchContactPage(params: ContactListParams, signal?: AbortSignal) {
  return fetchJson<ContactListPage>(`/api/contacts/list?${contactListSearch(params)}`, { signal });
}

/** A key `contactListKey` made. */
export const isContactListKey = (key: readonly unknown[]) => key[0] === "/api/contacts" && key[2] === "list";

/**
 * Pages of `CONTACT_LIST_PAGE`, the next one fetched when the page asks for it.
 * While a new search or filter is in flight the previous answer stays on screen
 * (`keepPreviousData`), so the list never blanks between keystrokes.
 *
 * Bounded by going back to the first page when invalidated off screen
 * (components/ui/infinite-list-trim.ts), not by `maxPages`: dropping pages
 * from the top of a list the reader is scrolling would move every row on
 * screen, and nothing above the first loaded row could bring them back.
 */
export function useContactList(params: ViewParams | null) {
  trimInfiniteListsWhenIdle(queryClient, isContactListKey);
  return useInfiniteQuery({
    queryKey: params ? contactListKey(params) : ["/api/contacts", null, "list"],
    queryFn: ({ pageParam, signal }) =>
      fetchContactPage({ ...(params as ViewParams), offset: pageParam, limit: CONTACT_LIST_PAGE }, signal),
    initialPageParam: 0,
    getNextPageParam: (last: ContactListPage) => last.nextOffset ?? undefined,
    enabled: !!params?.projectId,
    placeholderData: keepPreviousData,
  });
}

export function contactSummaryQuery(projectId: string | null | undefined, owner: string) {
  return {
    queryKey: contactSummaryKey(projectId, owner),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchJson<ContactSummary>(
        `/api/contacts/summary?${new URLSearchParams({ projectId: projectId ?? "", ...(owner !== "all" ? { owner } : {}) })}`,
        { signal },
      ),
    enabled: !!projectId,
  };
}

/** Every row of a list, in order, as the page has loaded it. */
export function flattenContactPages(data: ContactListData | undefined): Contact[] {
  return data?.pages.flatMap((p) => p.contacts) ?? [];
}

/**
 * Apply a change to every cached page of every list view of this project — an
 * edited contact must read the same in whichever view has it loaded.
 * Returning null removes the row, and moves that view's total down with it.
 */
export function updateCachedContactLists(projectId: string | null | undefined, fn: (row: Contact) => Contact | null) {
  queryClient.setQueriesData<ContactListData>({ queryKey: ["/api/contacts", projectId, "list"] }, (old) => {
    if (!old?.pages) return old;
    return {
      ...old,
      pages: old.pages.map((page) => {
        const contacts = page.contacts.map(fn).filter((c): c is Contact => c !== null);
        return { ...page, contacts, total: Math.max(0, page.total - (page.contacts.length - contacts.length)) };
      }),
    };
  });
}

/**
 * Read every row a view matches, for the export — in pages of 500, one after
 * another (the per-user rate limit is a per-minute budget, and a burst of
 * parallel pages is exactly what it is for).
 */
export async function fetchAllContactRows(
  params: ViewParams,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ contacts: Contact[]; total: number }> {
  const out: Contact[] = [];
  let offset: number | null = 0;
  let total = 0;
  while (offset !== null) {
    const page: ContactListPage = await fetchContactPage({ ...params, offset, limit: CONTACT_LIST_MAX_LIMIT });
    out.push(...page.contacts);
    total = page.total;
    onProgress?.(out.length, total);
    offset = page.nextOffset;
  }
  return { contacts: out, total };
}

/**
 * A contact picker's rows: the newest 30 until something is typed, then the
 * best 30 matches from the whole project. The pickers used to filter the full
 * downloaded list, which past 2,500 contacts could not offer the rest at all.
 */
export function useContactPicker(projectId: string | null | undefined, search: string, enabled: boolean) {
  const q = useDebounce(search.trim(), 200);
  return useQuery({
    queryKey: ["/api/contacts", projectId, "picker", q],
    queryFn: ({ signal }) =>
      fetchContactPage({ projectId: projectId!, q: q || undefined, sort: "createdAt", dir: "desc", limit: 30 }, signal),
    enabled: enabled && !!projectId,
    staleTime: 60_000,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === projectId ? prev : undefined),
    select: (page) => page.contacts,
  });
}
